import React, { useState, useEffect } from 'react';
import { FaTimes, FaLightbulb } from 'react-icons/fa';

/**
 * SaaS-style onboarding tooltip. Shows once per tip (persisted in localStorage).
 *
 * Props:
 *   id        - unique key for localStorage (e.g. "tip-connect-whatsapp")
 *   title     - bold heading (optional)
 *   children  - tip body text/JSX
 *   step      - step number to show (optional, e.g. "1 of 3")
 *   position  - "top" | "bottom" (default "bottom")
 *   className - additional CSS classes
 *   persist   - if true, always show (ignore dismissed state)
 */
export default function OnboardingTip({ id, title, children, step, position = 'bottom', className = '', persist = false }) {
  const storageKey = `walytic_tip_${id}`;
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (persist) {
      setDismissed(false);
      return;
    }
    const wasDismissed = localStorage.getItem(storageKey);
    setDismissed(!!wasDismissed);
  }, [storageKey, persist]);

  if (dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(storageKey, '1');
    setDismissed(true);
  };

  return (
    <div className={`relative bg-brand-50 border border-brand/20 rounded-lg px-4 py-3 ${className}`}>
      <div className="flex items-start gap-2.5">
        <FaLightbulb size={14} className="text-brand mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {(title || step) && (
            <div className="flex items-center gap-2 mb-0.5">
              {title && <span className="text-sm font-semibold text-ink-primary">{title}</span>}
              {step && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand/10 text-brand font-medium">{step}</span>}
            </div>
          )}
          <div className="text-xs text-ink-secondary leading-relaxed">{children}</div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-ink-faint hover:text-ink-secondary flex-shrink-0 mt-0.5"
          title="Dismiss tip"
        >
          <FaTimes size={10} />
        </button>
      </div>
    </div>
  );
}

/**
 * Inline helper text (always visible, no dismiss). Lighter than OnboardingTip.
 */
export function HelpText({ children, className = '' }) {
  return (
    <p className={`text-xs text-ink-muted flex items-start gap-1.5 ${className}`}>
      <FaLightbulb size={10} className="text-ink-faint mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </p>
  );
}
